import { useConnectionStore } from '@/stores/connection-store';
import { useTunnelStore } from '@/stores/tunnel-store';
import type { UserInfo, AuditLogEntry } from '@/types';

const BASE_URL = '';

/**
 * Detect if a successful API call implies a connection state change,
 * and sync connection + tunnel status to client stores.
 */
function syncConnectionStatus(path: string, body?: unknown): void {
  // Detect connection activity from schema/query calls
  let connectionId: string | undefined;

  const schemaMatch = path.match(/^\/api\/schema\/([^/]+)/);
  if (schemaMatch) {
    connectionId = schemaMatch[1];
  }

  if (path.startsWith('/api/query/execute') && body && typeof body === 'object') {
    const b = body as Record<string, unknown>;
    if (typeof b.connectionId === 'string') {
      connectionId = b.connectionId;
    }
  }

  if (connectionId) {
    const store = useConnectionStore.getState();
    if (store.activeConnections[connectionId]?.status !== 'connected') {
      store.setStatus(connectionId, 'connected');
    }
  }

  // Sync tunnel status whenever connections change (connect, disconnect, or data activity)
  const shouldSyncTunnels =
    connectionId ||
    path.match(/\/api\/connections\/[^/]+\/connect/) ||
    path.match(/\/api\/connections\/[^/]+\/disconnect/);

  if (shouldSyncTunnels) {
    syncActiveTunnels();
  }
}

/** Fetch active tunnels from server and sync to tunnel store.
 *  Marks running tunnels, AND marks previously-running tunnels as stopped. */
let tunnelSyncPending = false;
function syncActiveTunnels(): void {
  if (tunnelSyncPending) return;
  tunnelSyncPending = true;

  const token = localStorage.getItem('dbgo_token');
  if (!token) { tunnelSyncPending = false; return; }

  fetch('/api/tunnels/active', {
    headers: { 'Authorization': `Bearer ${token}` },
  })
    .then((res) => res.ok ? res.json() : [])
    .then((active: Array<{ tunnelId: string; localPort: number }>) => {
      const store = useTunnelStore.getState();
      const activeIds = new Set(active.map((t) => t.tunnelId));

      // Mark active tunnels as running
      for (const t of active) {
        if (store.activeTunnels[t.tunnelId]?.status !== 'running') {
          store.setStatus(t.tunnelId, 'running', { localPort: t.localPort });
        }
      }

      // Mark previously-running tunnels as stopped if no longer active
      for (const [tunnelId, tunnel] of Object.entries(store.activeTunnels)) {
        if (tunnel.status === 'running' && !activeIds.has(tunnelId)) {
          store.setStatus(tunnelId, 'stopped');
        }
      }
    })
    .catch(() => { /* ignore */ })
    .finally(() => { tunnelSyncPending = false; });
}

class ApiClient {
  private getToken(): string | null {
    return localStorage.getItem('dbgo_token');
  }

  private headers(): Record<string, string> {
    const h: Record<string, string> = { 'Content-Type': 'application/json' };
    const token = this.getToken();
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const res = await fetch(`${BASE_URL}${path}`, {
      method,
      headers: this.headers(),
      body: body ? JSON.stringify(body) : undefined,
    });

    if (res.status === 401) {
      localStorage.removeItem('dbgo_token');
      localStorage.removeItem('dbgo_user');
      // In Auth0 mode, redirect to root to trigger re-authentication
      if (import.meta.env.VITE_AUTH0_DOMAIN) {
        window.location.href = '/';
      } else {
        window.location.reload();
      }
      throw new Error('Unauthorized');
    }

    const text = await res.text();
    const data = text ? JSON.parse(text) : {};

    if (res.status === 403) {
      throw new Error((data as Record<string, string>).error || 'Access denied');
    }

    if (!res.ok) {
      throw new Error((data as Record<string, string>).error || `Request failed: ${res.status}`);
    }

    // Sync connection status for auto-reconnect detection
    syncConnectionStatus(path, body);

    return data as T;
  }

  get<T>(path: string): Promise<T> {
    return this.request<T>('GET', path);
  }

  post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('POST', path, body);
  }

  put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('PUT', path, body);
  }

  delete<T>(path: string): Promise<T> {
    return this.request<T>('DELETE', path);
  }

  // ═══════════════════════════════════════════
  // Auth
  // ═══════════════════════════════════════════

  async login(username: string, password: string): Promise<{ token: string; user: UserInfo }> {
    const result = await this.post<{ token: string; user: UserInfo }>('/api/auth/login', { username, password });
    localStorage.setItem('dbgo_token', result.token);
    localStorage.setItem('dbgo_user', JSON.stringify(result.user));
    return result;
  }

  async setup(username: string, password: string, displayName?: string): Promise<{ token: string; user: UserInfo }> {
    const result = await this.post<{ token: string; user: UserInfo }>('/api/auth/setup', { username, password, displayName });
    localStorage.setItem('dbgo_token', result.token);
    localStorage.setItem('dbgo_user', JSON.stringify(result.user));
    return result;
  }

  async checkSetup(): Promise<boolean> {
    const result = await this.get<{ needsSetup: boolean }>('/api/auth/setup-check');
    return result.needsSetup;
  }

  getCurrentUser(): UserInfo | null {
    const raw = localStorage.getItem('dbgo_user');
    if (!raw) return null;
    try {
      return JSON.parse(raw) as UserInfo;
    } catch {
      return null;
    }
  }

  /**
   * Logout handler registered by App.tsx when running in Auth0 mode. The
   * Auth0 SDK's `logout()` clears the SDK's own session storage (cookies +
   * in-memory cache) AND hits Auth0's end-session endpoint. Without going
   * through the SDK we'd only clear our `dbgo_token` and the SDK would
   * silently re-auth on the next page load — which is exactly what bit
   * the second user when she tried to sign out.
   */
  private auth0Logout: ((opts: { logoutParams: { returnTo: string } }) => void) | null = null;
  setAuth0Logout(fn: (opts: { logoutParams: { returnTo: string } }) => void): void {
    this.auth0Logout = fn;
  }

  logout(): void {
    localStorage.removeItem('dbgo_token');
    localStorage.removeItem('dbgo_user');

    if (this.auth0Logout) {
      // Auth0 SDK path — handles the end-session flow + cookie cleanup
      this.auth0Logout({ logoutParams: { returnTo: window.location.origin } });
      return;
    }

    // Local-auth mode (no Auth0): just reload to show the login form.
    window.location.reload();
  }

  // ═══════════════════════════════════════════
  // User Management (admin only)
  // ═══════════════════════════════════════════

  getUsers(): Promise<UserInfo[]> {
    return this.get<UserInfo[]>('/api/users');
  }

  createUser(data: { username: string; password: string; displayName?: string; role?: string }): Promise<UserInfo> {
    return this.post<UserInfo>('/api/users', data);
  }

  updateUser(id: string, data: { displayName?: string; password?: string; role?: string }): Promise<UserInfo> {
    return this.put<UserInfo>(`/api/users/${id}`, data);
  }

  deleteUser(id: string): Promise<{ ok: boolean }> {
    return this.delete<{ ok: boolean }>(`/api/users/${id}`);
  }

  getAuditLog(): Promise<AuditLogEntry[]> {
    return this.get<AuditLogEntry[]>('/api/users/audit-log');
  }
}

export const apiClient = new ApiClient();
